import { Suspense, type ReactNode } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Notification } from "@/components/admin/notification";
import { Error } from "@/components/admin/error";
import { Skeleton } from "@/components/ui/skeleton";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";

import { useConfigurationLoader } from "../root/useConfigurationLoader";
import { AppSidebar } from "./AppSidebar";
import { TopBar } from "./TopBar";

/**
 * The desktop shell (W12 U1): sidebar, top bar, and the page on a light grey
 * ground so its white cards stand out. The mobile layout is separate.
 */
export const Layout = ({ children }: { children: ReactNode }) => {
  useConfigurationLoader();
  return (
    <SidebarProvider>
      <SkipToContent />
      <AppSidebar />
      <SidebarInset className="min-w-0 bg-muted/40">
        <TopBar />
        <main
          className="mx-auto w-full min-w-0 max-w-screen-2xl flex-1 p-4 outline-none md:p-6"
          id="main-content"
          tabIndex={-1}
        >
          <ErrorBoundary FallbackComponent={Error}>
            <Suspense
              fallback={<Skeleton className="h-12 w-12 rounded-full" />}
            >
              {children}
            </Suspense>
          </ErrorBoundary>
        </main>
      </SidebarInset>
      <Notification />
    </SidebarProvider>
  );
};

/**
 * The first Tab stop: jumps past the sidebar to the page. A button, not an
 * `#main-content` link, because the hash router would read that as a route.
 */
function SkipToContent() {
  return (
    <button
      type="button"
      className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-50 focus:rounded-md focus:bg-primary focus:px-3 focus:py-2 focus:text-sm focus:text-primary-foreground"
      onClick={() => document.getElementById("main-content")?.focus()}
    >
      Skip to content
    </button>
  );
}
